import { isEventTargetOnElement } from 'utils/dom';
import { store } from '@redux/store';
import ModalStep from '../enums/modalStep';
import {
  selectQuestionResponses,
  selectSelectedSlot
} from '../redux/selectors';

const stepMap = new Map([
  [ModalStep.TourConfiguration.toString(), 'AddHomes'],
  [ModalStep.TermsAndConditions.toString(), 'T&C'],
  [ModalStep.IdentityVerification.toString(), 'Register'],
  [ModalStep.SignIn.toString(), 'Register'],
  [ModalStep.SignUp.toString(), 'Register'],
  [ModalStep.Questions.toString(), 'Rft'],
  [ModalStep.Confirmation.toString(), 'Rft']
]);

const uTourDialogElement = document.querySelector(
  '[data-self-guided-tour-dialog]'
);
const getUtourButtonEyeball = () =>
  uTourDialogElement.querySelector('[data-self-guided-tour-phone-toggle]');
const getUtourButtonContinue = () =>
  uTourDialogElement.querySelector('[data-self-guided-tour-primary-button]');
const getUtourButtonBack = () =>
  uTourDialogElement.querySelector('[data-self-guided-tour-secondary-button]');
const getUtourButtonClose = () =>
  uTourDialogElement.querySelector('[data-self-guided-tour-dialog-close]');
const getUtourPhoneNumber = () =>
  uTourDialogElement.querySelector('[data-self-guided-tour-phone]');

/**
 * @function getUtourPosition
 * @description Maps a given modal step to a human-readable position string for analytics tracking.
 *              Used to categorize user actions within different steps of the self-guided tour modal.
 * @param {string} step - The modal step identifier (typically ModalStep.*.toString()).
 * @returns {string} The analytics string corresponding to the modal step.
 */
const getUtourPosition = step => {
  const position = stepMap.get(step);
  if (position) {
    return position;
  }
  return 'SelectDate';
};

/**
 * @function getGoogleAnalyticsEventProperties
 * @description Determines the analytics event properties based on which self-guided tour button was clicked.
 *              Maps the clicked element and modal step context to a GA4 event property object.
 * @param {Event} params.event - The click event object.
 * @param {string} params.currentStep - The current modal step.
 * @param {string} params.previousStep - The previous modal step.
 * @returns {Object|null} The event properties object for GA4 tracking, or null if no relevant button was clicked.
 */
export const getGoogleAnalyticsEventProperties = ({
  event,
  currentStep,
  previousStep
}) => {
  const eyeballButton = getUtourButtonEyeball();
  if (isEventTargetOnElement(eyeballButton, event)) {
    return {
      button_name: 'PhoneNumberView',
      position: 'SelectDate'
    };
  }

  const phoneNumber = getUtourPhoneNumber();
  if (isEventTargetOnElement(phoneNumber, event)) {
    return {
      button_name: 'ClickToDial',
      position: 'SelectDate'
    };
  }

  const closeButton = getUtourButtonClose();
  if (isEventTargetOnElement(closeButton, event)) {
    return {
      click_target2: 'ClickX',
      position: getUtourPosition(currentStep)
    };
  }

  const continueButton = getUtourButtonContinue();
  if (isEventTargetOnElement(continueButton, event)) {
    const questionResponses = selectQuestionResponses(store.getState());
    if (
      previousStep === ModalStep.TermsAndConditions.toString() &&
      currentStep === ModalStep.Questions.toString() &&
      questionResponses.length > 0
    ) {
      return null;
    }

    const properties = {
      button_name: 'Continue',
      position: getUtourPosition(currentStep)
    };

    if (
      previousStep === ModalStep.TermsAndConditions.toString() &&
      questionResponses.length === 0
    ) {
      properties.position = 'T&C';
      properties.button_name = 'AcceptandContinue';
    }

    if (
      previousStep === ModalStep.TourConfiguration.toString() &&
      currentStep !== ModalStep.DateTimeSelection.toString()
    ) {
      properties.position = 'AddHomes';
    }

    if (previousStep === ModalStep.SpecSelection.toString()) {
      properties.button_name = 'AHContinue';
      properties.position = 'Available Homes';
    }

    return properties;
  }

  const backButton = getUtourButtonBack();
  if (isEventTargetOnElement(backButton, event)) {
    const buttonName =
      previousStep === ModalStep.DateTimeSelection.toString()
        ? 'CallForAssistance'
        : 'Back';
    return {
      button_name: buttonName,
      position: getUtourPosition(previousStep)
    };
  }

  return null;
};

/**
 * @function getSegmentEventProperties
 * @description Determines the event properties based on which self-guided tour button was clicked.
 *              Maps the clicked element and modal step context to a event property object.
 * @param {Event} params.event - The click event object.
 * @param {string} params.currentStep - The current modal step.
 * @param {string} params.previousStep - The previous modal step.
 * @returns {Object|Array|null} The event properties object for tracking, or null if no relevant button was clicked.
 */
export const getSegmentEventProperties = ({ event, currentStep }) => {
  const continueButton = getUtourButtonContinue();
  if (isEventTargetOnElement(continueButton, event)) {
    if (currentStep === ModalStep.SignIn.toString()) {
      return { eventName: 'Login' };
    }

    const questionResponses = selectQuestionResponses(store.getState());

    if (
      (currentStep === ModalStep.Questions.toString() ||
        currentStep === ModalStep.TermsAndConditions.toString()) &&
      questionResponses.length > 0
    ) {
      // If the current step is Confirmation, we need to get the selected slot information.
      const tourInformation = selectSelectedSlot(store.getState());
      const homeElements = document.querySelectorAll(
        '[data-self-guided-tour-homes-item]'
      );

      // find question text values either "Yes" or "No"
      const questionsOptions = questionResponses.filter(
        item =>
          item.questionText.toLowerCase() === 'yes' ||
          item.questionText.toLowerCase() === 'no' ||
          item.questionText.toLowerCase() === 'agent name'
      );

      const tourIsAgent = questionsOptions.find(item =>
        item.parentText.toLowerCase().includes('real estate agent')
      );

      const tourWorkingAgent = questionsOptions.find(item =>
        item.parentText.toLowerCase().includes('are you working with')
      );

      // loop through the home elementes and send the data to Segment
      return Array.from(homeElements).map(item => {
        const homeAttributes = {
          home_price: item.dataset.homePrice,
          home_type: item.dataset.homeType,
          plan_id: item.dataset.homePlanId,
          plan_name: item.dataset.homePlanName,
          primary_image_url: item.dataset.homePrimaryImageUrl,
          spec_id: item.dataset.homeSpecId,
          city: item.dataset.homeCity,
          state: item.dataset.homeState,
          zip: item.dataset.homeZip
        };

        const customProperties = {
          // Date the tour was scheduled
          isadded_tour: true,
          button_action: 'schedule my self tour',
          tour_date: new Date().toISOString(),
          scheduledtour_date: tourInformation.SpecTimezoneDate,
          ...homeAttributes
        };

        if (tourIsAgent) {
          customProperties.tour_is_agent = tourIsAgent.questionText;
        }

        if (tourWorkingAgent) {
          customProperties.tour_working_w_agent = tourWorkingAgent.questionText;
        }

        return {
          eventName: 'Lead Modal Form Submitted',
          data: customProperties
        };
      });
    }
  }

  return null;
};
